import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { GridColumnDefinition } from "../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../shared/grid/models/grid-configuration.model";
import { GridRequest } from "../../../../shared/grid/models/grid-request.model";
import { StringHelper } from "../../../../utilities/contracts/string-helper";
import { IBulkActionJobSummary } from "./bulk-action-job-summary.model";
import { BulkActionResultsService } from "./bulk-action-results.service";

/**
 * Bulk-action results page reached from a job-completion notification
 * (route: /bulkactions/bulkactionresults/:jobId?phase=Validate|Execute).
 * Shows the validation/execute summary and a paged per-chase results grid.
 */
@Component({
    selector: "app-bulk-action-results",
    templateUrl: "./bulk-action-results.component.html",
    styleUrls: ["./bulk-action-results.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkActionResultsComponent implements OnInit, OnDestroy {
    jobId: string;
    phase: string;
    summary: IBulkActionJobSummary;
    gridConfiguration: GridConfiguration;
    gridRequest: GridRequest;
    gridRefresh = new EventEmitter<null>();
    isExecuting = false;
    showExecuteConfirm = false;
    private isFirstLoad = true;
    private readonly sink = new SubSink();

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly service: BulkActionResultsService,
        private readonly messagingService: MessagingService,
        private readonly changeDetector: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // React to phase (query-param) changes even when Angular reuses this component instance —
        // e.g. clicking the Execute notification while already on the Validate results page.
        this.sink.add(this.route.queryParamMap.subscribe(params => {
            this.jobId = this.route.snapshot.paramMap.get("jobId");
            const phaseParam = params.get("phase");
            this.phase = StringHelper.isAvailable(phaseParam) ? phaseParam : "Validate";

            // Build the grid AFTER the summary resolves — its columns depend on the job's action
            // (Move → workflow status, Reassign → assigned user).
            this.loadSummary();
            this.changeDetector.markForCheck();
        }));
    }

    ngOnDestroy(): void {
        this.sink.unsubscribe();
    }

    get isExecutePhase(): boolean {
        return (this.phase || "").toLowerCase() === "execute";
    }

    /**
     * Whether Execute has already been triggered for this job (queued, running, completed or
     * failed). The Validate results page stays reachable forever from its notification, so a user
     * can come back to it long after executing — this is what stops a second run.
     */
    get isExecuteStarted(): boolean {
        return !!this.summary && StringHelper.isAvailable(this.summary.executeStatus);
    }

    /** Why the Execute button is disabled, shown next to it. Empty when it's clickable. */
    get executeDisabledReason(): string {
        if (!this.summary || this.canExecute || this.isExecutePhase) {
            return "";
        }

        if (this.isExecuteStarted) {
            return `This bulk action was already executed (${this.summary.executeStatus}). Start a new bulk action to run it again.`;
        }

        if ((this.summary.status || "").toLowerCase() !== "completed") {
            return "Validation has not finished yet.";
        }

        if (this.summary.success < 1) {
            return "No chases passed validation, so there is nothing to execute.";
        }

        return "";
    }

    /**
     * Execute is offered only on a completed Validate phase that has at least one passed chase and
     * has not already been executed.
     */
    get canExecute(): boolean {
        return !this.isExecutePhase
            && !!this.summary
            && (this.summary.status || "").toLowerCase() === "completed"
            && this.summary.success > 0
            && !this.isExecuteStarted
            && !this.isExecuting;
    }

    openExecuteConfirm(): void {
        this.showExecuteConfirm = true;
    }

    execute(): void {
        this.isExecuting = true;
        this.service.executeJob(this.jobId).subscribe(
            () => {
                this.messagingService.showToast(
                    "Execute started. You'll be notified when it completes.", SeverityType.SUCCESS);
                // Re-read the summary so executeStatus reflects the now-queued Execute phase — that,
                // not the in-memory isExecuting flag, is what keeps the button disabled on a reload.
                this.loadSummary();
                this.changeDetector.markForCheck();
            },
            () => {
                this.isExecuting = false;
                this.messagingService.showToast(
                    "Failed to start Execute. Please try again.", SeverityType.ERROR);
                this.changeDetector.markForCheck();
            });
    }

    private loadSummary(): void {
        this.service.getSummary(this.jobId, this.phase).subscribe(
            summary => {
                this.summary = summary;
                this.buildGrid();
            },
            () => {
                // Summary failed — still render the grid (default Move columns) so the page isn't stuck.
                this.buildGrid();
            });
    }

    /** (Re)builds the grid once the summary is known; columns vary by action type. */
    private buildGrid(): void {
        this.createGrid();
        if (!this.isFirstLoad) {
            this.gridRefresh.emit();
        }
        this.isFirstLoad = false;
        this.changeDetector.markForCheck();
    }

    private createGrid(): void {
        const isReassign = (this.summary?.actionType || "").toLowerCase() === "reassign";
        const actionColumns = isReassign
            ? [
                new GridColumnDefinition({ field: "currentAssignedUser", header: "Current Assigned To User" }),
                new GridColumnDefinition({ field: "newAssignedUser", header: "New Assigned To User" }),
            ]
            : [
                new GridColumnDefinition({ field: "currentWorkflowStatus", header: "Current Workflow Status" }),
                new GridColumnDefinition({ field: "newWorkflowStatus", header: "New Workflow Status" }),
            ];

        this.gridConfiguration = new GridConfiguration({
            columns: [
                new GridColumnDefinition({ field: "chase", header: "Chase" }),
                new GridColumnDefinition({ field: "chaseKey", header: "Chase Key" }),
                ...actionColumns,
                new GridColumnDefinition({ field: "validationStatus", header: "Validation Status" }),
                new GridColumnDefinition({ field: "comments", header: "Comments" }),
            ],
            pageSize: 25,
            showActionColumn: false,
            trackByField: "rowNum",
        });

        this.gridRequest = new GridRequest({
            url: this.service.getResultsUrl(this.jobId, this.phase),
        });
    }
}
